import {metaInfo} from '@configs';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getCategoryLabel,
  getConfigByApp,
  getIdBySlug,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  utilServerSideDeviceDetection,
} from '@utils';
import cookie from 'cookie';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {JobDetail as JD} from '@jobseeker/common-core/types';

import {
  COOKIE_LOGIN_AUTH,
  HIRIST_CATEGORY_BLOG_ID_MAPPING,
  HIRIST_CATEGORY_ID_MAPPING,
} from '@jobseeker/common-core/constants';
import {
  dataService,
  jobDetailService,
  jobfeedService,
  userService,
} from '@jobseeker/common-core/services';
import {AppState, wrapper} from '@jobseeker/common-core/stores';

import {VersionVariants} from '@jobseeker/lotus/enums';

import {Chat} from '@components';
import {JOBDETAIL, JOBFEED} from '@constants';
import {loadSEO} from '@helpers';
import {getCookiesToDelete, setSSRCookies} from '@helpers/ssrMap';
import jobFeedService from '@jobseeker/common-core/services/jobfeed.service';
import {getExperienceIndex} from '@jobseeker/common-core/utils/common';
import {getDetailSEOContent} from '@utils/seo';

import HiristJobDetail from '@components/JobDetail';
import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import {AdsElementType} from '@jobseeker/common-core/types';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {cleanUrl, getFilteredCategoryList} from '@jobseeker/lotus/utils';
import type {GetServerSidePropsContext} from 'next/types';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useRef, useState} from 'react';

import {gladiatorService} from '@jobseeker/common-core/services';
import NotFoundPage from '../404';
import JobDetail from '@layouts/Body/Jobdetail';
import {Box, Typography} from '@mui/material';
import {
  getAppendedLinkForBreadcrumb,
  getBreadcrumbsElements,
} from '@layouts/ResponsiveLayout';
import {BreadcrumbJsonLd} from 'next-seo';
import {Breadcrumbs} from '@jobseeker/lotus/components';
import {BrandedJDData} from '@components/BrandedJD/type';

const PLATFORM = process?.env?.NEXT_PUBLIC_APP_NAME ?? '';
const isAppHirist = PLATFORM === 'hirist';

type JobDetailPageProps = {
  userIdSSR: number;
  jobId: string;
  jobMandateInfo: {
    jobTitle: string;
    jobCategory: number;
  };
  brandedJD?: number;
  brandedS3?: string;
  metaData: /* eslint-disable @typescript-eslint/no-explicit-any */ any;
  seoConfig: object;
  refInfo?: string;
  isMobile?: boolean;
  categoryName?: string;
  show410?: boolean; // <-- add this prop
};

const JobDetailPage = ({
  userIdSSR,
  jobId,
  jobMandateInfo,
  metaData,
  seoConfig,
  refInfo,
  brandedJD,
  brandedS3,
  isMobile,
  categoryName = '',
  show410 = false, // <-- default value
}: JobDetailPageProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  dispatch({
    type: 'AppContext/set_recap',
    payload: jobId,
  });

  const category = getCategoryLabel(
    metaData?.header?.meta?.tabsList,
    jobMandateInfo?.jobCategory
  );
  const [brandedJDData, setbrandedJDData] = useState<BrandedJDData>({});
  const [isBrandedDataLoaded, setIsBrandedDataLoaded] = useState(false);

  const getBrandedData = async () => {
    try {
      if (brandedJD === 0) setIsBrandedDataLoaded(true);
      if (brandedJD !== 1 || !brandedS3) return;
      const response = await dataService.getBrandeddata(brandedS3 || '');
      const data = response.data;
      setbrandedJDData(data);
      console.log('Fetched JSON:', data);
    } catch (error) {
      console.error('Error fetching JSON:', error);
    } finally {
      setIsBrandedDataLoaded(true);
    }
  };

  useEffect(() => {
    getBrandedData();
  }, []);

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: isAppHirist ? categoryName : category?.label,
      item: isAppHirist
        ? `/c/${cleanUrl(categoryName)}-jobs?&ref=breadcrumb_jd`
        : category?.tag
        ? `/c/${category?.tag
            .replace('&', '')
            .replace(/\s+/g, '-')
            .toLowerCase()}-jobs?&ref=breadcrumb_jd`
        : '',
    },
    {
      name: jobMandateInfo?.jobTitle?.split(',')[0]?.split('(')[0]?.trim(),
      item: '',
    },
  ];

  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  const detail: JB = useSelector((state: AppState) => state.jobDetail);
  const {tabsList} = metaData?.header?.meta || {};

  const [jobs, setJobs] = useState<JD[]>([]);
  useEffect(() => {
    const fetchCompanyJobs = async () => {
      const data = await jobfeedService.getSimilarJobs({
        userId,
        id: detail.id,
        cat: detail.categoryId,
        tags: detail.tags?.map((x: {id: number}) => x.id?.toString()) ?? '',
        exp: getExperienceIndex(detail?.min) || undefined,
        functionalArea: detail?.functionalArea,
        min: detail?.min,
        size: 8,
        companyIds: detail?.recruiter?.centralizedCompanyId.toString() || '',
        ...(isAppHirist && {whatFor: 'hirist'}),
      });

      setJobs(data?.data);
    };
    if (brandedJD == 1) {
      fetchCompanyJobs();
    }
  }, []);
  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  const breadCrumbs = getBreadcrumbsElements(
    breadcrumbElements,
    isAppHirist,
    device
  );
  const JobCategory = useMemo(() => {
    const tags = [];
    const categoryId = detail.categoryId;
    for (let i = 0; i < tabsList.length; i++) {
      if (tabsList[i]?.dataId == categoryId) {
        return tabsList[i];
      }
      tags.push(...tabsList[i].menu);
    }

    for (let j = 0; j < tags.length; j++) {
      if (tags[j].dataId == categoryId) {
        return tags[j];
      }
    }
  }, [detail.categoryId]);
  const hasTracked = useRef(false);

  const isBranded = brandedJD === 1;

  useEffect(() => {
    if (hasTracked.current) return;

    const sessionUserId = userIdSSR || userId || '-1';

    if (!isBrandedDataLoaded) return;
    let visibleKeysString = Object.entries(brandedJDData)
      .filter(([, value]: [string, any]) => value?.visibility === true)
      .map(([key, value]: [string, any]) => {
        if (key === 'banner') {
          if (value?.preferenceKey === 'bannerVideo') {
            return 'Video Banner';
          } else return 'Image Banner';
        } else if (key === 'aboutCompany') return 'AboutCompany';
        else if (key === 'awardsRecognitions') return 'Awards';
        else if (key === 'benefits') return 'Benefits';
        else if (key === 'keyHighlights') return 'KeyHighlights';
        else if (key === 'reviews') return 'Reviews';
      })
      .join(',');
    if (jobs && jobs?.length > 0) {
      if (visibleKeysString.length > 0) {
        visibleKeysString += ',';
      }
      visibleKeysString += 'SimilarJobs';
    }

    hasTracked.current = true;
    TrackEventUBA('jobDescriptionPageView', 'view', {
      ref: router.query.ref || '',
      jobID: detail.id,
      jobName: detail.title,
      categoryID: detail.categoryId,
      categoryName: JobCategory?.label,
      pageName: 'JobDescriptionPage',
      sessionUsername: sessionUserId,
      isBranded: isBranded ? 'Yes' : 'No',
      templateID: isBranded
        ? brandedJDData?.templateInfo?.templateNo || ''
        : '',
      sections: isBranded ? visibleKeysString : '',
      companyID: detail?.companyData?.companyId,
      centralCompanyID: detail?.recruiter?.centralizedCompanyId,
      companyName: detail?.companyData?.companyName,
    });

    if (
      isBranded &&
      brandedJDData?.banner &&
      brandedJDData?.banner?.visibility
    ) {
      TrackEventUBA('widgetView', 'view', {
        ref: router.query.ref || '',
        pageName: 'JD',
        jobID: detail.id,
        page: 'JD',
        templateID: brandedJDData?.templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: detail?.companyData?.companyId,
        centralCompanyID: detail?.recruiter?.centralizedCompanyId,
        companyName: detail?.companyData?.companyName,
        section: `${
          brandedJDData?.banner?.preferenceKey === 'bannerVideo'
            ? 'Video'
            : 'Image'
        } Banner`,
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
    }
  }, [userId, detail?.id, isBrandedDataLoaded]);

  if (show410) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} />
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={
              category && !isBranded ? breadcrumbElements : []
            }
          >
            {isAppHirist ? (
              <HiristJobDetail
                jobId={jobId}
                refInfo={refInfo}
                categoryName={categoryName}
                isMobile={isMobile}
                brandedData={brandedJDData}
                brandedJD={brandedJD}
                similarJobs={jobs}
              />
            ) : (
              <JobDetail
                dispatch={dispatch}
                showFeatured={false}
                showLearning={false}
                jobId={jobId}
                refInfo={refInfo}
                brandedData={brandedJDData}
                brandedJD={brandedJD}
                similarJobs={jobs}
              />
            )}
            {isBranded && (
              <Box
                sx={{
                  mb: -11,
                  mt: 7,
                  display: {xxs: 'none', md: 'block'},
                }}
              >
                {breadcrumbElements?.length > 0 && (
                  <BreadcrumbJsonLd
                    itemListElements={getAppendedLinkForBreadcrumb(
                      breadcrumbElements
                    )}
                  />
                )}
                {breadcrumbElements?.length > 0 && (
                  <Box
                    sx={
                      !isAppHirist
                        ? {pb: 2}
                        : {
                            borderBottom: '1px solid #f2f2f2',
                            borderTop: '1px solid #e6e6e6',
                            py: '9px',
                            pr: '50px',
                            pl: 2,
                            height: '34px',
                            width: '100%',
                            maxWidth: '1440px',
                            zIndex: 3,
                            // position: 'fixed',
                            background: '#fff',
                            boxShadow: '0 4px 15px 0 rgba(0,0,0,.06)',
                          }
                    }
                  >
                    <Breadcrumbs
                      variant={VersionVariants.VERSION_TWO}
                      separator={
                        <Typography
                          fontSize={10}
                          color={!isAppHirist ? 'secondary.main' : '#898989'}
                          mx={!isAppHirist ? 0 : 1.25}
                        >
                          {'>'}
                        </Typography>
                      }
                      breadcrumbs={breadCrumbs}
                    />
                  </Box>
                )}
              </Box>
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const jobSlug = context.params.jobslug;
    const applyDialogue = context.query.apply;
    const refInfo = context.query.ref || '';
    const jobSlugUrl = context?.resolvedUrl || '';
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBDETAIL);

    const jobId: string = jobSlug.split('-')?.pop() || '';

    let userId = -1;
    let shouldClearAuth = false;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          // Handle logout/refresh response from validation
          if (
            validateInfo?.type &&
            ['refresh', 'logout'].includes(validateInfo.type)
          ) {
            if (validateInfo.type === 'refresh' && validateInfo.token) {
              console.log('🚀 ~ Authentication validation returned refresh');
              const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
                validateInfo.token
              );

              context.res?.setHeader('Set-Cookie', [
                ssrTokenCookie,
                ssrLastActCookie,
              ]);

              // Update request headers with refreshed cookies for subsequent API calls
              if (context.req?.headers) {
                context.req.headers.cookie = `${ssrTokenCookie}; ${ssrLastActCookie}`;
              }

              // Use the refreshed token for subsequent calls
              userId = parseInt(validateInfo?.userId ?? '0', 10);
            }

            if (validateInfo.type === 'logout') {
              console.log('🚀 ~ Authentication validation returned logout');
              shouldClearAuth = true;
              userId = -1;

              // Clear auth state in store
              store.dispatch({
                type: 'Auth/set_user_id',
                payload: -1,
              });
              store.dispatch({
                type: 'Auth/set_pro',
                payload: false,
              });
            }
          } else {
            userId = parseInt(validateInfo?.userId ?? '0', 10);
          }
        }
      }
    } catch (err) {
      // Handle any unexpected errors
      console.log('🚀 ~ Authentication validation error:', err);
      shouldClearAuth = true;
      userId = -1;
    }

    const {
      header: {
        meta: {tabsList},
      },
      ads: {learnGrow, featuredInstitutes},
    } = metaData || {};

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: JOBDETAIL},
    });

    store.dispatch({
      type: 'AppContext/set_jd_apply',
      payload: /true/.test(applyDialogue),
    });

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobdetail/set_initial_state'});

    const jobMandateInfo = {
      id: 0,
      jobTitle: '',
      jobCategory: '',
      jobTags: '',
      functionalArea: '',
      brandedJD: 0,
      brandedUrl: '',

      expMin: 0,
      companyName: '',
      location: '',
      exp: '',
      confidential: 0,
    };

    const {isMobile} = utilServerSideDeviceDetection(context);
    console.log('🚀 ~ store=> ~ JD --> isMobile:', isMobile);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const isBotUser = isUserAgentForBot(context);

    let categoryName = '';
    let jobDetails: JB | any = {};

    try {
      let headers = {};

      if (context.req?.headers?.cookie && !shouldClearAuth) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );
        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          headers = {
            cookie: context.req?.headers?.cookie,
          };

          const proRes = await userService.checkPro('checkpro', {headers});
          console.log(
            '🚀 ~ file: [jobslug].tsx:150 ~ store=> ~ proRes:',
            proRes
          );
          if (proRes?.type && ['refresh', 'logout'].includes(proRes.type)) {
            if (proRes?.type === 'refresh' && proRes?.token) {
              const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
                proRes.token
              );

              context.res?.setHeader('Set-Cookie', [
                ssrTokenCookie,
                ssrLastActCookie,
              ]);
            }
            if (proRes?.type === 'logout') {
              const cookiesTodelete = getCookiesToDelete();
              context.res?.setHeader('Set-Cookie', cookiesTodelete);
              headers = {
                cookie: '',
              };
              shouldClearAuth = true;
            }
          } else {
            let payload = proRes?.pro;

            if (isAppHirist) {
              payload = true;
            }

            store.dispatch({type: 'Auth/set_pro', payload});
          }
        }
      }

      jobDetails = await jobDetailService.getJobDetails({
        jobId,
        headers,
      });

      jobMandateInfo.brandedJD = jobDetails?.brandedJd || 0;
      jobMandateInfo.brandedUrl = jobDetails?.brandedS3 || '';
      console.log({xyzz: jobDetails?.brandedS3});

      if (jobDetails?.refJobId) {
        const jobUrl = `${jobSlugUrl?.replace(
          `-${jobId}`,
          `-${jobDetails?.refJobId}`
        )}`;

        return {
          redirect: {
            permanent: true,
            destination: refInfo ? `${jobUrl}?ref=${refInfo}` : jobUrl,
          },
          props: {},
        };
      }

      store.dispatch({
        type: 'job/jobdetail/set_jobdetail',
        payload: jobDetails,
      });

      jobMandateInfo.id = jobDetails?.id;
      jobMandateInfo.jobTags =
        jobDetails?.tags?.map((x: {id: number}) => x.id?.toString()) ?? '';
      jobMandateInfo.expMin = jobDetails?.min ?? 0;
      jobMandateInfo.jobTitle = jobDetails?.title;
      jobMandateInfo.jobCategory = jobDetails?.categoryId;
      jobMandateInfo.confidential = jobDetails?.confidential ?? 0;
      jobMandateInfo.functionalArea = jobDetails?.functionalArea ?? 0;

      if (jobDetails?.categoryId > -1) {
        let categoryInfo: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
          {};

        if (isAppHirist) {
          const categoryMappedList = getFilteredCategoryList(tabsList);

          categoryInfo = categoryMappedList.find(
            cat => cat.dataId == jobDetails?.categoryId
          );

          categoryName = categoryInfo?.label;
        } else {
          const categoryId = jobDetails.categoryId;

          const cat = (() => {
            const tags = [];

            for (let i = 0; i < tabsList.length; i++) {
              if (tabsList[i]?.dataId == categoryId) {
                return tabsList[i];
              }
              tags.push(...tabsList[i].menu);
            }

            for (let j = 0; j < tags.length; j++) {
              if (tags[j].dataId == categoryId) {
                return tags[j];
              }
            }

            return null;
          })();
          categoryName = `${cat.tag
            ?.toLowerCase()
            .replace(/[^A-Z0-9]+/gi, '-')}-jobs`;
        }

        const {id, title, catId, slug} = getIdBySlug(
          'category',
          isAppHirist
            ? `${categoryInfo?.tag?.replace(' ', '-')}-jobs`
            : jobMandateInfo.jobCategory.toString(),
          tabsList,
          !isAppHirist,
          isAppHirist
        );

        const slugCategoryId = isAppHirist
          ? catId && catId > 0
            ? catId
            : id
          : jobDetails?.categoryId;

        if (slugCategoryId > -1) {
          store.dispatch({
            type: 'AppContext/set_navigation_selector',
            payload: slugCategoryId,
          });

          store.dispatch({
            type: 'AppContext/set_category_actual',
            payload: jobDetails?.categoryId,
          });

          if (isAppHirist) {
            const CATEGORY_MAPPED =
              HIRIST_CATEGORY_BLOG_ID_MAPPING[
                HIRIST_CATEGORY_ID_MAPPING[slugCategoryId?.toString() || ''] ??
                  slugCategoryId
              ];
            const blogs = await jobfeedService.getLatestBlogs(
              CATEGORY_MAPPED ?? ''
            );
            const bloData = (Object.values(blogs) as AdsElementType[])
              ?.map(blog => {
                return {
                  link: blog?.link,
                  title: blog?.title,
                  imageUrl: blog?.featuredImage ?? '',
                };
              })
              ?.slice(0, 3);

            store.dispatch({
              type: 'Ads/set_blog_data',
              payload: bloData,
            });
          }
        }

        store.dispatch({
          type: 'AppContext/set_slug',
          payload: title,
        });
        store.dispatch({
          type: 'AppContext/set_slug_url',
          payload: slug,
        });

        if (
          (jobDetails?.permanentlyRemoved || jobDetails?.state === 2) &&
          !jobDetails?.applied
        ) {
          if (context.res) {
            context.res.statusCode = 410; // <-- set 410 status code
          }
          return {
            props: {
              show410: true,
              jobId,
              jobMandateInfo,
              metaData,
              seoConfig: {},
              refInfo,
              isMobile,
              categoryName,
            },
          };
        }

        if (jobDetails?.hasExpired && !jobDetails?.applied) {
          return {
            redirect: {
              destination: `/c/${cleanUrl(
                categoryName
              )}?ref=${refInfo}&expJD=1`,
              permanent: true,
            },
          };
        }
      }

      jobDetails?.locations?.map((locationInfo: {id: string; name: string}) => {
        jobMandateInfo.location = `${jobMandateInfo.location} ${locationInfo?.name}`;
      });
      jobMandateInfo.exp = `(${jobDetails?.min}-${jobDetails?.max})`;
      jobMandateInfo.jobTitle = jobDetails?.title;
      jobMandateInfo.companyName = jobDetails?.companyData?.companyName || '';
    } catch (error: /* eslint-disable @typescript-eslint/no-explicit-any */ any) {
      console.log('🚀 ~ store=> ~ error:', error);
      if (
        error?.response?.data?.error?.message ===
        'Could not find job with given jobcode'
      ) {
        store.dispatch({
          type: 'job/jobdetail/set_jobdetail',
          payload: {},
        });

        return {
          props: {
            jobId,
            jobMandateInfo,
            metaData,
          },
        };
      }
      return {
        notFound: true,
      };
    }

    if (isBotUser) {
      try {
        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: jobMandateInfo.jobCategory || '',
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );
          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: jobMandateInfo.jobCategory || '',
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        try {
          /**
           * Fetch the initial state for jobs list and related ads to show
           */
          const {
            auth: {userId},
          } = store.getState();

          const jobFeeds = await jobFeedService.getSimilarJobs({
            userId,
            id: jobMandateInfo.id,
            cat: jobMandateInfo.jobCategory,
            tags: jobMandateInfo.jobTags,
            exp: getExperienceIndex(jobMandateInfo?.expMin) || undefined,
            functionalArea: jobMandateInfo?.functionalArea,
            min: jobMandateInfo?.expMin,
            size: 100,
            ...(isAppHirist && {whatFor: 'hirist'}),
          });

          store.dispatch({
            type: 'job/jobFeed/set_jobfeed',
            payload: {totalJobs: 0, ...jobFeeds},
          });

          store.dispatch({
            type: 'job/jobFeed/set_loadMore',
            payload: false,
          });
        } catch (error) {
          console.log(
            '🚀 ~ file: [slug].tsx:200 ~ store jobdetail similar jobs=> ~ error:',
            error
          );
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ job details Ads API error:',
          error
        );

        return {
          notFound: true,
        };
      }
    }

    const seoConfig = await loadSEO(
      JOBDETAIL,
      getDetailSEOContent(jobMandateInfo),
      jobSlugUrl?.split('?')[0],
      true
    );

    if (!jobDetails?.active) {
      seoConfig.noindex = true;
      seoConfig.nofollow = true;
    }

    // Clear cookies if authentication failed
    if (shouldClearAuth) {
      const cookiesTodelete = getCookiesToDelete();
      context.res?.setHeader('Set-Cookie', cookiesTodelete);
    }

    return {
      props: {
        userIdSSR: userId,
        jobId,
        jobMandateInfo,
        seoConfig,
        metaData,
        refInfo,
        isMobile,
        categoryName,
        brandedJD: jobMandateInfo?.brandedJD,
        brandedS3: jobMandateInfo?.brandedUrl,
      },
    };
  }
);

export default JobDetailPage;
